import axios from "axios";
import jwtAuthAxios from "../auth/jwtAuth";
import jwtAuthKioskAxios from "../auth/jwtAuthKiosk";
import { replaceAll } from "app/utils/stringHelpers";
import { sanitizeObject } from "app/utils/sanitizeObject";
const prepareFormData = (data) => {
  const bodyFormData = new FormData();

  Object.keys(data).forEach((field) => {
    const isObject =
      typeof data[field] === "object" &&
      data[field] !== null &&
      !(data[field] instanceof File);
    const value = isObject ? JSON.stringify(data[field]) : data[field];
    bodyFormData.append(field, value);
  });

  return bodyFormData;
};

export const getCustomers = async (customerId) => {
  try {
    setAuthBackendDomain(jwtAuthAxios);
    const { data } = await jwtAuthAxios.get(
      `/customers/list?customerId=${customerId}`,
    );
    return data.response;
  } catch (error) {
    return error;
  }
};
export const getClients = async (clientId) => {
  try {
    if (clientId) {
      const response = await jwtAuthKioskAxios.get(
        `/customer/clients/${clientId}`,
      );
      return response?.data?.data;
    } else {
      const response = await jwtAuthKioskAxios.get(`/customer/clients`);
      return response?.data?.data;
    }
  } catch (error) {
    return error;
  }
};

export const updateClient = async (clientId, client) => {
  const { data } = await jwtAuthKioskAxios.put(
    `/customer/clients/${clientId}`,
    client,
  );
  return data?.result;
};

export const createClient = async (values) => {
  const { data } = await jwtAuthKioskAxios.post(`/customer/clients`, values);
  return data;
};

export const getCustomerEnv = () => {
  const dsokSettings = JSON.parse(localStorage.getItem("dsokSettings"));
  console.log("domain", dsokSettings?.customer?.beDomain);
  return dsokSettings?.customer?.beDomain ===
    "https://staging.gastrokiosk.online"
    ? true
    : false;
};

/**
 * @returns the base url of the customer
 */
/**
 * Keď AdminUI beží na localhoste, VŽDY sa volá lokálny backend (localhost:8080),
 * nezávisle od REACT_APP_ENVIRONMENT (build-time, môže byť zastaraný) aj od
 * per-customer beDomain. Robustné pre lokálny vývoj/testovanie.
 * @returns {string|null} backend URL alebo null (nie localhost)
 */
const localhostBackendOrNull = () => {
  let host = "";
  try {
    host = (window?.location?.hostname || "").toLowerCase();
  } catch (e) {
    host = "";
  }
  if (host === "localhost" || host === "127.0.0.1") {
    return "http://localhost:8080";
  }
  return null;
};

export const getBEUrl = () => {
  const local = localhostBackendOrNull();
  if (local) return local;
  const env = process.env.REACT_APP_ENVIRONMENT.trim();
  const backendDomain = process.env.REACT_APP_BE_URL.trim();
  // return backendDomain;
  const user = JSON.parse(localStorage.getItem("user"));
  const dsokSettings = user?.customer?.dsokSettings;

  switch (env) {
    case "dev":
      return process.env.REACT_APP_BE_URL.trim();
    case "prod":
    case "staging":
      return dsokSettings?.customer?.beDomain || backendDomain;
    default:
      return backendDomain;
  }
};

export const getBEUrlForAdmin = (beDomain) => {
  const local = localhostBackendOrNull();
  if (local) return local;
  const env = process.env.REACT_APP_ENVIRONMENT.trim();
  const backendDomain = process.env.REACT_APP_BE_URL.trim();
  // return backendDomain;

  switch (env) {
    case "dev":
      return process.env.REACT_APP_BE_URL.trim();
    case "prod":
    case "staging":
      return beDomain || backendDomain;
    default:
      return backendDomain;
  }
};

/**
 * @param {import("axios").AxiosInstance} jwtAuthKioskAxios
 */
export const setBackendDomain = (jwtAuthKioskAxios) => {
  const backendDomain = getBEUrl();
  jwtAuthKioskAxios.defaults.baseURL = `${backendDomain}/`;
};

export const setAuthBackendDomain = (jwtAuthAxios) => {
  jwtAuthAxios.defaults.baseURL = `${process.env.REACT_APP_DATAVISION_BACKEND_URL}/V1`;
};

/**
 * Fetches aggregated admin dashboard data from {{DATAVISION_BE}}/api/V1/dashboard.
 * @param {Object} params - caller, severity, status, limitProblems, limitActivity
 * @returns {Promise<{ overview: object, problems: array, recentOrders: array, recentSyncErrors: array }>}
 */
export const getAdminDashboard = async (params = {}) => {
  setBackendDomain(jwtAuthAxios);
  try {
    const { data } = await jwtAuthAxios.get("/api/V1/dashboard", {
      params: {
        caller: "admin",
        severity: params.severity !== undefined ? params.severity : "warning",
        status: params.status !== undefined ? params.status : "new",
        limitProblems: params.limitProblems ?? 100,
        limitActivity: params.limitActivity ?? 100,
      },
    });
    const raw = data?.data ?? data?.response ?? data ?? {};
    return mapDashboardResponse(raw);
  } catch (error) {
    return error;
  }
};

function mapDashboardResponse(raw) {
  const kpis = raw.kpis ?? raw.overview ?? {};
  const overview = {
    activeCustomers: kpis.activeCustomersCount ?? kpis.activeCustomers ?? 0,
    activeDevices: kpis.activeDevicesCount ?? kpis.activeDevices ?? 0,
    devicesOnline: kpis.devicesOnlineCount ?? kpis.devicesOnline ?? 0,
    devicesOffline: kpis.devicesOfflineCount ?? kpis.devicesOffline ?? 0,
    devicesInMaintenance:
      kpis.devicesMaintenanceCount ?? kpis.devicesInMaintenance ?? 0,
    errorsLast24h: kpis.errorsLast24hCount ?? kpis.errorsLast24h ?? 0,
    customersWithProblems:
      kpis.customersWithProblemCount ?? kpis.customersWithProblems ?? 0,
  };
  const problemsRaw = Array.isArray(raw.problems) ? raw.problems : [];
  const problems = problemsRaw.map((p) => ({
    id: p.id,
    customerName: p.customerName ?? p.customer_name,
    customerUrl: p.customerUrl ?? p.customer_url,
    locationName: p.locationName ?? p.location_name,
    deviceName: p.deviceName ?? p.device_name,
    problemType: p.problemType ?? p.problem_type,
    severity: p.severity,
    lastOccurrence:
      p.lastOccurrenceAt ?? p.lastOccurrence ?? p.lastOccurrenceTimestamp,
    status: p.status,
  }));
  const activity = raw.activity ?? {};
  const recentOrders = Array.isArray(activity.recentOrders)
    ? activity.recentOrders
    : Array.isArray(raw.recentOrders)
      ? raw.recentOrders
      : [];
  const recentSyncErrors = Array.isArray(activity.recentSyncErrors)
    ? activity.recentSyncErrors
    : Array.isArray(raw.recentSyncErrors)
      ? raw.recentSyncErrors
      : [];
  return {
    overview,
    problems,
    recentOrders: recentOrders.map((o) => ({
      id: o.id,
      orderNumber: o.orderNumber ?? o.order_number,
      customerName: o.customerName ?? o.customer_name,
      customerUrl: o.customerUrl ?? o.customer_url,
      time: o.time ?? o.createdAt ?? o.created_at,
      amount: o.amount,
    })),
    recentSyncErrors: recentSyncErrors.map((e) => ({
      id: e.id,
      customerName: e.customerName ?? e.customer_name,
      customerUrl: e.customerUrl ?? e.customer_url,
      deviceName: e.deviceName ?? e.device_name,
      errorType: e.errorType ?? e.error_type,
      time: e.time ?? e.occurredAt ?? e.occurred_at,
    })),
  };
}

export const getCustomer = async (customerUrl) => {
  setAuthBackendDomain(jwtAuthAxios);
  try {
    const { data } = await jwtAuthAxios.get(
      `/customer?customer=${customerUrl}`,
    );
    return data?.response;
  } catch (error) {
    return error.response.data;
  }
};

export const getDSOKCategories = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(`/dsok/categories`);
  return data.categories;
};

export const getDSOKCategoriesByCustomerId = async (customerId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(
    `/dsok/categories/customer/${customerId}`,
  );
  return data.categories;
};

export const getDSOKOrders = async (params = {}) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { limit, offset, includeMeta, ...rest } = params || {};
  const { data } = await jwtAuthKioskAxios.get(`/dsok/customer-orders`, {
    params: {
      ...rest,
      ...(typeof limit === "number" ? { limit } : {}),
      ...(typeof offset === "number" ? { offset } : {}),
    },
  });
  return includeMeta ? data : data.orders;
};

export const getDSOKOrdersForStatusScreen = async (
  locationCode,
  env = "prod",
  preparationStatus = "all",
) => {
  setJwtAuthKioskAxiosDomain(jwtAuthKioskAxios, env);
  const { data } = await jwtAuthKioskAxios.get(
    `/dsok/customer-orders-status-screen/${locationCode}/${preparationStatus}`,
  );
  // Note: Orders should be returned in their natural order, not sorted by ID
  return data;
};

export const setJwtAuthKioskAxiosDomain = (jwtAuthKioskAxios, env) => {
  switch (env) {
    case "prod":
      jwtAuthKioskAxios.defaults.baseURL = `https://be.gastrokiosk.online`;
      break;
    case "stage":
      jwtAuthKioskAxios.defaults.baseURL = `https://staging.gastrokiosk.online`;
      break;
    case "dev":
      jwtAuthKioskAxios.defaults.baseURL = `http://localhost:8080`;
      break;
    default:
      jwtAuthKioskAxios.defaults.baseURL = `https://be.gastrokiosk.online`;
      break;
  }
};

export const getCallScreenOrders = async (
  customerCode,
  locationId,
  baseURL,
) => {
  const posConfig = {
    method: "GET",
    url: `${baseURL}/easyPay/call-screen/${customerCode}/${locationId}`,
    headers: {
      "Content-Type": "application/json",
    },
  };
  const response = await axios(posConfig);
  return response.data;
};

export const getEasyPayOrders = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(`/dsok/customer-easypay-orders`);
  return data.orders;
};

export const updateEasyPaiOrderProcessStaus = async (order, status) => {
  const updatedOrder = { ...order, processStatus: status };
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/customer-easypay-order-update`,
    updatedOrder,
  );
  return data?.result;
};

export const updateOrderPreparationStatus = async (
  orderId,
  preparationStatus,
  customerCode,
  env,
  customerId,
) => {
  setJwtAuthKioskAxiosDomain(jwtAuthKioskAxios, env);
  const { data } = await jwtAuthKioskAxios.put(
    `/dsok/order/updateOrderPreparationStatus`,
    {
      orderId,
      preparationStatus,
      customerCode,
      customerId,
    },
  );
  return data;
};

// Zmena stavu prípravy objednávky cez oficiálne V1 API. Na tento endpoint je
// naviazaný celý notifikačný systém (outbox → notif. server → sledovacia
// stránka / SMS), preto dodržiavame jeho kontrakt presne:
//   PUT /api/v1/orders/preparation-status
//   { customerId|customerCode, status, orderNumber, cancelReason?, source, changedBy? }
// orderNumber sa na serveri hľadá v _id | externalOrderId | orderCode.
// cancelReason je povinný pri status="canceled" (objekt { code, note } alebo kód).
export const changeOrderPreparationStatus = async ({
  orderNumber,
  status,
  customerId,
  customerCode,
  cancelReason,
  changedBy,
}) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.put(
    `/api/v1/orders/preparation-status`,
    {
      orderNumber,
      status,
      source: "admin",
      ...(customerId != null && customerId !== ""
        ? { customerId }
        : customerCode != null && customerCode !== ""
          ? { customerCode }
          : {}),
      ...(cancelReason ? { cancelReason } : {}),
      ...(changedBy ? { changedBy } : {}),
    },
  );
  return data;
};

// Znovu odošle notifikáciu pre aktuálny (notifikovateľný) stav objednávky.
//   POST /api/v1/orders/resend-notification
export const resendOrderNotification = async ({
  orderNumber,
  customerId,
  customerCode,
}) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/api/v1/orders/resend-notification`,
    {
      orderNumber,
      ...(customerId != null && customerId !== ""
        ? { customerId }
        : customerCode != null && customerCode !== ""
          ? { customerCode }
          : {}),
    },
  );
  return data;
};
// Storno (reversal) platby objednávky.
//   POST /api/v1/orders/payment/reversal  { orderNumber, customerId?, changedBy?, source:"admin" }
export const reversePayment = async ({
  orderNumber,
  customerId,
  customerCode,
  changedBy,
}) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/api/v1/orders/payment/reversal`,
    {
      orderNumber,
      source: "admin",
      ...(customerId != null && customerId !== ""
        ? { customerId }
        : customerCode != null && customerCode !== ""
          ? { customerCode }
          : {}),
      ...(changedBy ? { changedBy } : {}),
    },
  );
  return data;
};

// Hromadná zmena stavu prípravy viacerých objednávok (výdajová obrazovka).
export const updateOrderPreparationStatusBatch = async (
  orderIds,
  preparationStatus,
  customerCode,
  env,
  customerId,
  cancelReason,
) => {
  setJwtAuthKioskAxiosDomain(jwtAuthKioskAxios, env);
  const { data } = await jwtAuthKioskAxios.put(
    `/dsok/order/updateOrderPreparationStatusBatch`,
    {
      orderIds,
      preparationStatus,
      customerCode,
      customerId,
      ...(cancelReason ? { cancelReason } : {}),
    },
  );
  return data;
};

export const upsertDSOKCategory = async (category) => {
  const bodyFormData = prepareFormData(category);
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/categories`,
    bodyFormData,
    {
      headers: { "Content-Type": "multipart/form-data" },
    },
  );
  return data?.result;
};

export const deleteDSOKCategory = async (categoryId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.delete(
    `/dsok/categories/${categoryId}`,
  );
  return data?.result;
};

export const exportDSOKCategoriesCsv = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const response = await jwtAuthKioskAxios.get(`/dsok/category/export-csv`, {
    responseType: "blob",
  });
  return response.data;
};

export const previewDSOKCategoriesImportCsv = async (file) => {
  setBackendDomain(jwtAuthKioskAxios);
  const bodyFormData = new FormData();
  bodyFormData.append("file", file);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/category/import-csv/preview`,
    bodyFormData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
};

export const commitDSOKCategoriesImportCsv = async (file, offset, limit) => {
  setBackendDomain(jwtAuthKioskAxios);
  const bodyFormData = new FormData();
  bodyFormData.append("file", file);
  if (offset != null) bodyFormData.append("offset", String(offset));
  if (limit != null) bodyFormData.append("limit", String(limit));
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/category/import-csv/commit`,
    bodyFormData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
};

/**
 *
 * @param {*} dsok
 * @returns
 */
export const editDSOKDetails = async (dsok) => {
  // store customer DSOK settings to the MySQL database
  setAuthBackendDomain(jwtAuthAxios);
  const sanitizedDsok = sanitizeObject(dsok);
  if (
    process.env.NODE_ENV === "development" &&
    JSON.stringify(sanitizedDsok) !== JSON.stringify(dsok)
  ) {
    console.warn("Sanitized input: removed invalid characters");
  }
  const { data } = await jwtAuthAxios.put(`/customer/dsok`, sanitizedDsok);
  const response = await getCustomer(data.url);
  // Normalize dsokSettings: Symfony may return it as a raw JSON string from
  // the MySQL TEXT column. Parse it into an object so Axios re-serializes it
  // cleanly — without unescaped double quotes or other encoding issues.
  const normalizedResponse =
    response && typeof response === "object" ? { ...response } : response;
  if (
    normalizedResponse &&
    typeof normalizedResponse.dsokSettings === "string"
  ) {
    try {
      normalizedResponse.dsokSettings = JSON.parse(
        normalizedResponse.dsokSettings,
      );
    } catch {
      normalizedResponse.dsokSettings = null;
    }
  }
  // store customer DSOK settings to the MongoDB database
  setBackendDomain(jwtAuthKioskAxios);
  await jwtAuthKioskAxios.post(
    `/dsok/customer/updateDsokSettingsTempFile`,
    normalizedResponse,
  );
  return data?.result;
};

export const getDeviceStatus = async (deviceId, customerUrl) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(`/dsok/deviceStatus`, {
    params: { deviceId, customerUrl },
  });
  return data;
};

export const getDSOKProducts = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(`/dsok/product/list`);
  return data.products;
};

export const deleteDSOKProduct = async (productId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.delete(`/dsok/product/${productId}`);
  return data?.result;
};

export const syncDSOKProducts = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(`/dsok/product/generateJson`);
  return data?.result;
};

export const upsertDSOKProduct = async (product) => {
  setBackendDomain(jwtAuthKioskAxios);
  // Decide transport: if there is a File anywhere, use multipart/form-data, else send JSON
  const hasFile = Object.values(product || {}).some((v) => v instanceof File);
  let data;
  if (hasFile) {
    if (typeof product.image === "string") {
      delete product.image;
    }
    const bodyFormData = prepareFormData(product);
    ({ data } = await jwtAuthKioskAxios.post(`/dsok/product`, bodyFormData, {
      headers: { "Content-Type": "multipart/form-data" },
    }));
  } else {
    ({ data } = await jwtAuthKioskAxios.post(`/dsok/product`, product));
  }
  // Backend responds with { code, status, id } (id = inserted/updated product id),
  // so return the whole payload; callers can read data.id (e.g. to open a newly
  // created product's detail after "save & stay").
  return data;
};

export const copyDSOKProduct = async (productId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(`/dsok/product/copy`, {
    productId,
  });
  return data?.result;
};

export const exportDSOKProductsCsv = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const response = await jwtAuthKioskAxios.get(`/dsok/product/export-csv`, {
    responseType: "blob",
  });
  return response.data;
};

export const previewDSOKProductsImportCsv = async (file) => {
  setBackendDomain(jwtAuthKioskAxios);
  const bodyFormData = new FormData();
  bodyFormData.append("file", file);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/product/import-csv/preview`,
    bodyFormData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
};

export const commitDSOKProductsImportCsv = async (file, offset, limit) => {
  setBackendDomain(jwtAuthKioskAxios);
  const bodyFormData = new FormData();
  bodyFormData.append("file", file);
  if (offset != null) bodyFormData.append("offset", String(offset));
  if (limit != null) bodyFormData.append("limit", String(limit));
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/product/import-csv/commit`,
    bodyFormData,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return data;
};

export const getDSOKProductAttributes = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get(`/dsok/product-attribute/list`);
  return data.attributes;
};

export const deleteDSOKProductAttribute = async (attributeId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.delete(
    `/dsok/product-attribute/${attributeId}`,
  );
  return data?.result;
};

export const upsertDSOKProductAttribute = async (attribute) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/dsok/product-attribute`,
    attribute,
  );
  return data?.result;
};

export const triggerDSOKBuild = async (customer) => {
  const { id, dsokSettings, bearerToken } = customer;
  const dsok = JSON.parse(dsokSettings);
  const siteEnv =
    dsok?.customer?.beDomain === "https://staging.gastrokiosk.online"
      ? "staging"
      : "production";
  const branch =
    dsok?.customer?.beDomain === "https://staging.gastrokiosk.online"
      ? "staging"
      : "master";
  const { data } = await axios.get(
    `https://jenkins.gastrokiosk.online/job/DSOK FE/buildWithParameters`,
    {
      params: {
        token: "4xtPWFEx4cJgxWWfNd",
        CUSTOMER_ID: id,
        CUSTOMER_CODE: replaceAll(dsok.customer.code, "-", ""),
        SITE_ENV: siteEnv,
        BEARER_TOKEN: bearerToken,
        BRANCH: branch,
      },
    },
  );
  return data;
};

export const getBuildConfigurations = async () => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.get("/dsok/buildConfiguration");
  return data?.buildConfigurations;
};

export const upsertBuildConfiguration = async (customer) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    "/dsok/buildConfiguration",
    customer,
  );
  return data?.result;
};

/**
 * Opätovná registrácia fiškálneho dokladu do eKasy.
 * @param {string} id - _id objednávky
 * @param {{ printCopy?: boolean, forceWithoutVerification?: boolean, changedBy?: string }} [options]
 *   printCopy: ak doklad už existuje, vytlačiť kópiu; forceWithoutVerification: admin potvrdil,
 *   že doklad v eKase neexistuje (backend ho použije len keď overenie v Portose nie je možné)
 * @returns {Promise<object>} telo odpovede (status, statusCode, receiptId, printCopy…);
 *   pri chybe vyhodí axios error s response.data = { statusCode, reason, error… }
 */
/**
 * Tlač kópie zaregistrovaného eKasa Portos dokladu na kiosku (SmartKiosk / DLA).
 * @returns {Promise<object>} telo odpovede; pri chybe axios error s response.data = { statusCode, reason, error }
 */
export const sendEkasaPrintCopy = async (id, changedBy) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post("/dsok/order/ekasa-print-copy", {
    id,
    changedBy: changedBy || null,
  });
  return data;
};

export const sendEkasaRegister = async (id, options = {}) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post("/dsok/order/ekasa-register", {
    id,
    printCopy: options.printCopy === true,
    forceWithoutVerification: options.forceWithoutVerification === true,
    changedBy: options.changedBy || null,
  });
  return data;
};

export const sendOrderToExternalSystem = async (orderId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    "/dsok/order/external-system-register",
    {
      orderId,
    },
  );
  return data?.result;
};

export const generateToken = async (customerId) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post("/customer/generateToken", {
    customerId,
  });
  return data?.token;
};

export const toggleDeviceMaintenance = async (deviceId, maintenance) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post(
    `/api/v1/customer/device/maintenance`,
    { deviceId, maintenance },
  );
  return data;
};

export const triggerSonetSettlement = async (deviceUUID, beDomain, customerId) => {
  jwtAuthKioskAxios.defaults.baseURL = `${getBEUrlForAdmin(beDomain)}/`;
  const { data } = await jwtAuthKioskAxios.post(
    `/api/v1/customer/device/sonet/settlement`,
    { deviceUUID, customerId },
  );
  return data;
};

export const fetchDSOKProducts = async (url) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.post("/dsok/fetchProducts", { url });
  return data;
};

export const deleteDSOKOrder = async (id) => {
  setBackendDomain(jwtAuthKioskAxios);
  const { data } = await jwtAuthKioskAxios.delete(`/dsok/order/${id}`);
  return data?.result;
};

export const getDotykackaBranch = async (customerId, beDomain) => {
  jwtAuthKioskAxios.defaults.baseURL = `${getBEUrlForAdmin(beDomain)}/`;
  const { data } = await jwtAuthKioskAxios.get(
    `/integration/dotykacka/branch/${customerId}`,
  );
  return data;
};

export const getDotykackaProducts = async (customerId, beDomain) => {
  jwtAuthKioskAxios.defaults.baseURL = `${getBEUrlForAdmin(beDomain)}/`;
  const { data } = await jwtAuthKioskAxios.get(
    `/integration/dotykacka/products/${customerId}`,
  );
  return data;
};

/**
 * Fetches notification delivery status (SMS/push) for an order from the
 * notification service (proxied by the backend). Used to show the Brevo
 * delivery timeline in the order detail.
 * @param {string|number} externalOrderId
 * @returns {Promise<{ orderId: string, deliveries: Array }>}
 */
export const getOrderNotificationStatus = async (externalOrderId) => {
  setBackendDomain(jwtAuthAxios);
  const { data } = await jwtAuthAxios.get(
    `/api/V1/orders/${externalOrderId}/notifications`
  );
  return data?.result ?? { orderId: String(externalOrderId), deliveries: [] };
};
